import { useEffect, type ReactNode } from "react";
import { useApolloClient, useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { ExtractionJobResultDocument, PowersDataDocument } from "@/graphql/generated/operations";
import { ExtractionJobStatus } from "@/graphql/generated/types";
import { formatDateTime } from "@/lib/format-date";
import { isMissingEntityError } from "@/lib/graphql-error";
import { userErrorMessage } from "@/lib/user-error";
import { ExtractionDialogFrame } from "./ExtractionDialogFrame";
import { ExtractionResultView } from "./ExtractionResultView";
import { refreshProfileSkills, resultOrStandIn, unscoredPowerCount } from "./extraction-run-changes";

/** A Power- or Hardskill-Analyse reopened from its completion notification (spark#1216). */
export function ExtractionResultDialog({ jobId, onClose }: { jobId: string; onClose: () => void }) {
  const intl = useIntl();
  const apollo = useApolloClient();
  const { data, error } = useQuery(ExtractionJobResultDocument, {
    variables: { jobId },
    fetchPolicy: "network-only",
  });
  const job = data?.extractionJob;
  const finished = job?.status === ExtractionJobStatus.Completed || job?.status === ExtractionJobStatus.Failed;
  // Read now, not taken from the run: how many powers have no score is a state fact, as on the live panel.
  const powers = useQuery(PowersDataDocument, {
    skip: !finished || !job?.extractPowers,
    fetchPolicy: "network-only",
  });
  // The profile behind the dialog may have loaded before the run wrote its skills.
  const skillsWritten = finished && Boolean(job?.extractSkills);
  useEffect(() => {
    if (skillsWritten) refreshProfileSkills(apollo);
  }, [skillsWritten, apollo]);

  // Jobs from before genesis recorded the flags carry neither.
  const title = job?.extractPowers
    ? intl.formatMessage({ id: "service.page.powerAnalysisTitle", defaultMessage: "Power analysis" })
    : job?.extractSkills
      ? intl.formatMessage({ id: "service.page.hardskillAnalysisTitle", defaultMessage: "Hard skill analysis" })
      : intl.formatMessage({ id: "save.extraction.reopen.title", defaultMessage: "Document analysis" });

  let body: ReactNode;
  if (error) {
    // genesis answers an expired, an unknown and someone else's job alike.
    body = isMissingEntityError(error) ? (
      <div id="analysis-expired" className="text-smycles-label text-ink-muted">
        <FormattedMessage
          id="save.extraction.reopen.expired"
          defaultMessage="This analysis result is no longer available. Results stay available for 30 days."
        />
      </div>
    ) : (
      <div className="text-smycles-label text-danger">{userErrorMessage(intl, error)}</div>
    );
  } else if (!job) {
    body = (
      <div role="status" className="text-smycles-label text-ink-muted">
        <FormattedMessage id="common.loading" defaultMessage="Loading…" />
      </div>
    );
  } else if (!finished) {
    body = (
      <div className="text-smycles-label text-ink-muted">
        <FormattedMessage
          id="save.extraction.runningInBackground"
          defaultMessage="Running in the background - you can close this and keep working. You’ll be notified when it’s done."
        />
      </div>
    );
  } else {
    body = (
      <>
        <div className="smycles-field-meta text-ink-muted">
          <FormattedMessage
            id="save.extraction.reopen.startedAt"
            defaultMessage="Started {date}"
            values={{ date: formatDateTime(job.createdAt, intl.locale) }}
          />
        </div>
        <ExtractionResultView
          result={resultOrStandIn(job.result, job.status === ExtractionJobStatus.Completed)}
          job={job}
          unscoredPowers={powers.data ? unscoredPowerCount(powers.data.smartAnalysisResults) : null}
        />
      </>
    );
  }

  return (
    <ExtractionDialogFrame id="analysis" title={title} onClose={onClose} dataGraphql="ExtractionJobResult, PowersData"
      footer={
        <div className="flex justify-end border-t border-ink/10 px-5 py-3">
          <Button id="analysis-done" variant="primary" size="compact" onClick={onClose}>
            <FormattedMessage id="save.extraction.done" defaultMessage="Done" />
          </Button>
        </div>
      }>
      {body}
    </ExtractionDialogFrame>
  );
}
